/** /regions/:id/:tab? — region editor shell. Tabs: General + Used by. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as regionsApi from '@/api/regions';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { regionsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';

import { cardArrangementActions, copyActions, type CatalogCtx } from '../catalog/catalogLinks';

import { RegionGeneralTab } from './tabs/RegionGeneralTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function RegionDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: region,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['regions', 'detail', id],
    queryFn: () => regionsApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => void refetch();

  const deleteMutation = useMutation({
    mutationFn: () => regionsApi.remove(id),
    onSuccess: () => {
      snackbar.show('Region deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['regions'] });
      navigate('/regions');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid region id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !region) return <Alert severity="error">Couldn’t load region.</Alert>;

  const ctx: CatalogCtx = { tenantId: region.tenant?.id };
  const arrangementLabel = region.card_arrangement_display || region.card_arrangement;

  return (
    <Box>
      <PageHeader
        helpStory="regions-trip-types"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={region.name}
        subtitle={
          <IdentitySubtitle
            tenant={region.tenant}
            grid={region.tenant ? { href: regionsHref({ tenant: region.tenant.id }), label: 'Regions of this operator' } : null}
            id={region.id}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <ValueActions chip actions={cardArrangementActions(region.card_arrangement, arrangementLabel, ctx)}>
              <Chip size="small" variant="outlined" label={arrangementLabel} />
            </ValueActions>
            <PermissionGate perm="console_regions_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      {/* `ext_id` is in the payload and was never rendered; regions have no
          entry in the ext-ids `model` vocabulary, so it only copies. */}
      {region.ext_id && (
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: -1.5, mb: 2 }}>
          <DetailSubtitle
            parts={[
              <ValueActions key="ext" actions={copyActions(region.ext_id, 'Copy external id')}>
                Ext ID {region.ext_id}
              </ValueActions>,
            ]}
          />
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/regions/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <RegionGeneralTab region={region} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedByPanel resource="regions" id={region.id} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.region" objectPk={String(region.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to={regionsHref({ tenant: region.tenant?.id })} style={{ color: '#0a1a3f' }}>
          ← Back to regions
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this region?"
        message={`"${region.name}" will be deleted. This cannot be undone.`}
        confirmLabel="Delete region"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
